import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosApi } from '@app/api/axios.api';

interface ActivityState {
  activities: Record<string, {
    data: any[];
    totalRows: number;
    loading: boolean;
    error: string | null;
  }>;
}

const initialState: ActivityState = {
  activities: {}
};

// Create a key for caching based on entity type and ID
const createCacheKey = (entityType: string, entityId: number | string) => 
  `${entityType}-${entityId}`;

export const fetchActivities = createAsyncThunk(
  'activities/fetch',
  async ({ 
    entityType,
    entityId, 
    page, 
    pageSize, 
    searchQuery 
  }: { 
    entityType: string;
    entityId: number | string;
    page: number;
    pageSize: number;
    searchQuery?: string;
  }) => {
    const response = await axiosApi.get(`/api/${entityType}/${entityId}/activities`, {
      params: {
        page,
        per_page: pageSize,
        q: searchQuery,
      }
    });
    return {
      data: response.data,
      cacheKey: createCacheKey(entityType, entityId)
    };
  }
);

const activitySlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    clearActivityData: (state, action) => {
      const cacheKey = createCacheKey(action.payload.entityType, action.payload.entityId);
      delete state.activities[cacheKey];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivities.pending, (state, action) => {
        const { entityType, entityId } = action.meta.arg;
        const cacheKey = createCacheKey(entityType, entityId);
        
        state.activities[cacheKey] = {
          ...state.activities[cacheKey],
          loading: true,
          error: null
        };
      })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        const { cacheKey } = action.payload;
        const {rows, total_rows} = action.payload.data;

        const data = rows.map((item: any) => item.attributes) || [];

        state.activities[cacheKey] = {
          data: data,
          totalRows: total_rows,
          loading: false,
          error: null
        };
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        const { entityType, entityId } = action.meta.arg;
        const cacheKey = createCacheKey(entityType, entityId);
        
        state.activities[cacheKey] = {
          ...state.activities[cacheKey],
          loading: false,
          error: action.error.message || 'Failed to fetch activities'
        };
      });
  },
});

export const { clearActivityData } = activitySlice.actions;
export default activitySlice.reducer;